// Окно создания и правки (components/Modal/README.md) на Radix Dialog: Tab ходит по кругу внутри
// окна, Esc и клик по затемнению закрывают верхний слой, фокус возвращается на элемент, который
// открыл окно. До 640 px окно выезжает снизу.
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useRef } from 'react';
import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { cx } from '../lib/cx.ts';
import { keepOpenForToasts } from '../lib/layers.ts';
import { modKey } from '../lib/platform.ts';
import { useReturnFocus } from '../lib/return-focus.ts';
import { ToastLayerRegion } from '../lib/toast-layer.tsx';
import { Button } from './Button.tsx';
import { IconButton } from './IconButton.tsx';
import { Kbd } from './Kbd.tsx';

export type ModalSize = 'sm' | 'md' | 'lg';

const sizeClass: Record<ModalSize, string | undefined> = {
  sm: 'kp-modal-sm',
  md: undefined,
  lg: 'kp-modal-lg',
};

/**
 * Куда встаёт фокус при открытии: отмеченное data-autofocus (в теле или подвале), иначе первое
 * поле ввода, иначе первая кнопка тела окна.
 */
const AUTOFOCUS = '[data-autofocus]:not([disabled])';
const FIRST_FIELD =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])';
const FIRST_FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Заголовок dialog-title. */
  title: ReactNode;
  /** Одна фраза о назначении окна. */
  description?: ReactNode;
  /** 420 px — подтверждение и одно поле, 520 px — форма, 720 px — справка. */
  size?: ModalSize;
  /** Подвал: ModalFooter. */
  footer?: ReactNode;
  /** ⌘ ↵ на Mac, Ctrl ↵ на остальных — отправка длинной формы; слушаются обе клавиши. */
  onSubmitShortcut?: () => void;
  /** Вложенный редактор может обработать Escape, не закрывая всю форму. */
  onEscapeKeyDown?: (event: globalThis.KeyboardEvent) => void;
  /** Куда вернуть фокус после закрытия, если окно открыли из меню; иначе — откуда открыли. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  children?: ReactNode;
  className?: string;
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  footer,
  onSubmitShortcut,
  onEscapeKeyDown,
  returnFocusRef,
  children,
  className,
}: ModalProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocus = useReturnFocus(open, contentRef, { returnFocusRef });

  const focusFirst = (event: Event) => {
    const body = bodyRef.current;
    const target =
      contentRef.current?.querySelector<HTMLElement>(AUTOFOCUS) ??
      body?.querySelector<HTMLElement>(FIRST_FIELD) ??
      body?.querySelector<HTMLElement>(FIRST_FOCUSABLE);
    if (target) {
      event.preventDefault();
      target.focus();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (onSubmitShortcut && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onSubmitShortcut();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="kp-overlay">
          <Dialog.Content
            ref={contentRef}
            className={cx('t-modal', sizeClass[size], className)}
            onOpenAutoFocus={focusFirst}
            onCloseAutoFocus={returnFocus}
            onKeyDown={onKeyDown}
            onEscapeKeyDown={onEscapeKeyDown}
            onPointerDownOutside={keepOpenForToasts}
            onInteractOutside={keepOpenForToasts}
            {...(description == null ? { 'aria-describedby': undefined } : {})}
          >
            <div className="t-modal-h">
              <div className="kp-grow">
                <Dialog.Title asChild>
                  <h2>{title}</h2>
                </Dialog.Title>
                {description != null ? (
                  <Dialog.Description asChild>
                    <p>{description}</p>
                  </Dialog.Description>
                ) : null}
              </div>
              <Dialog.Close asChild>
                <IconButton icon={X} label="Закрыть" shortcut="Esc" size="sm" />
              </Dialog.Close>
            </div>
            <div ref={bodyRef} className="t-modal-b">
              {children}
            </div>
            {footer}
            <ToastLayerRegion active={open} />
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ModalFooterProps {
  /** Слева: «Создать ещё», «Скопировать ссылку». */
  secondary?: ReactNode;
  /** Подсказка клавиши отправки перед кнопками: ⌘ ↵ на Mac, Ctrl ↵ на остальных (FR-037). */
  submitHint?: boolean;
  /** «Отмена»; null — кнопки нет (всё применяется сразу). */
  cancelLabel?: string | null;
  onCancel?: () => void;
  /** Главная кнопка справа. */
  primary?: ReactNode;
}

/** Подвал окна: вторичное слева → распорка → «Отмена» → главное справа. */
export function ModalFooter({
  secondary,
  submitHint = false,
  cancelLabel = 'Отмена',
  onCancel,
  primary,
}: ModalFooterProps) {
  return (
    <div className="t-modal-f">
      {secondary}
      <span className="sp" />
      {submitHint ? (
        <span className="kp-modal-hint kp-hide-phone" aria-hidden="true">
          <Kbd>{modKey()}</Kbd>
          <Kbd>↵</Kbd>
        </span>
      ) : null}
      {cancelLabel !== null ? (
        onCancel ? (
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
        ) : (
          <Dialog.Close asChild>
            <Button variant="secondary">{cancelLabel}</Button>
          </Dialog.Close>
        )
      ) : null}
      {primary}
    </div>
  );
}
